// BM-H80 P2 — FormShell.
//
// Layout wrapper for the client-facing form pages: header with matter
// ref, sticky progress bar, "save and return later" pill and a
// "request a callback" button. The page passes ``sections`` for the
// progress and ``activeSection`` to highlight; the shell does not
// know anything about the underlying form field schema.

import { PhoneCall, Save } from "lucide-react";

import { FIRM_NAME } from "../../config/constants";
import "./FormShell.css";

// eslint-disable-next-line react/prop-types
export default function FormShell({
  matterRef,
  clientName,
  sections,
  activeSection,
  onSaveAndReturn,
  onRequestCallback,
  children,
  rightRail,
  // BM-H3545 - found live in a real browser render (jsdom component tests
  // never check this outer chrome text): FormShell hardcoded the Wills
  // questionnaire wording regardless of which form it wraps, so the LPA
  // page showed will-specific chrome while displaying an LP1H form.
  // Defaults match the prior behaviour, so ClientFormWills.jsx needs no
  // change; ClientFormLPA.jsx now passes the LPA-specific values. Also
  // switched the firm name itself to the FIRM_NAME white-label constant -
  // it was hardcoded here, which the firm-strings guard (rightly) flags.
  questionnaireLabel = "Wills questionnaire",
  documentNoun = "will",
}) {
  const total = sections.length;
  const activeIdx = Math.max(0, sections.findIndex((s) => s.id === activeSection));
  const pct = total > 0 ? Math.round(((activeIdx + 1) / total) * 100) : 0;

  return (
    <div
      data-testid="client-form-shell"
      className="client-form-surface"
      style={{
        maxWidth: 1200,
        margin: "24px auto 64px",
        padding: "28px 28px 72px",
      }}
    >
      <header style={{ marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 }}>
              {FIRM_NAME} · {questionnaireLabel}
            </div>
            <h1 style={{ fontSize: 24, margin: "4px 0 0", color: "#0f172a" }}>
              {clientName ? `${clientName}'s ${documentNoun}` : `Your ${documentNoun}`}
            </h1>
            <div data-testid="client-form-matter-ref" style={{ fontSize: 13, color: "#475569", marginTop: 2 }}>
              Matter reference {matterRef}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              data-testid="client-form-save-btn"
              onClick={onSaveAndReturn}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                background: "#fff",
                border: "1px solid #0f766e",
                color: "#0f766e",
                borderRadius: 999,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <Save size={14} /> Save and return later
            </button>
            <button
              type="button"
              data-testid="client-form-callback-btn"
              onClick={onRequestCallback}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "8px 14px",
                background: "#0f766e",
                border: "1px solid #0f766e",
                color: "#fff",
                borderRadius: 999,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <PhoneCall size={14} /> Request a callback
            </button>
          </div>
        </div>

        <nav
          data-testid="client-form-progress"
          aria-label="Form progress"
          style={{
            marginTop: 20,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div style={{ flex: 1, height: 6, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" }}>
            <div
              data-testid="client-form-progress-fill"
              style={{
                width: `${pct}%`,
                height: "100%",
                background: "#0f766e",
                transition: "width 240ms ease",
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: "#334155" }}>
            Section {activeIdx + 1} of {total}
          </div>
        </nav>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: rightRail ? "minmax(0, 1fr) 320px" : "minmax(0, 1fr)", gap: 24 }}>
        <main data-testid="client-form-main">{children}</main>
        {rightRail && (
          <div style={{ position: "sticky", top: 20, alignSelf: "start" }}>
            {rightRail}
          </div>
        )}
      </div>
    </div>
  );
}
